import { Component, EventEmitter, Input, OnDestroy, Output, ChangeDetectionStrategy } from '@angular/core';
import {
  Invoice,
  InvoiceStatus,
  PaymentFrequency,
  PetInsuranceData, YearListItem
} from "../../../models";
import { ApiHttpService } from "../../../services/api-http.service";
import { DataStorageService } from "../../../services/data-storage.service";
import { DocumentViewerService } from "../../../services/document-viewer.service";
import { invoicePdfErrorMessageKeys, parseDateCreated } from "../../../utils";
import { Subject, takeUntil } from "rxjs";

@Component({
  selector: 'cpf-invoice-list',
  templateUrl: './invoice-list.component.html',
  styleUrls: ['./invoice-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class InvoiceListComponent implements OnDestroy {
  @Output() invoiceClicked = new EventEmitter<Invoice>();

  public invoices: Invoice[] = [];
  public invoiceYearList: YearListItem<Invoice>[] = [];
  public petData?: PetInsuranceData;

  private destroy$ = new Subject<void>();
  /** Ids of the invoices whose PDF is currently being fetched. */
  private downloading = new Set<string>();

  constructor(private dataStorageService: DataStorageService,
              private documentViewerService: DocumentViewerService,
              private apiHttpService: ApiHttpService) {
    this.setPetData();

    this.dataStorageService.selectedData$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.setPetData();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setPetData() {
    const selectedData = this.dataStorageService.selectedData;

    if (selectedData) {
      this.petData = selectedData;
      this.invoices = (selectedData.invoices || []).filter(i => i.invoiceStatus !== InvoiceStatus.FUTURE);
      if (this.invoices.length > 0) {
        this.invoiceYearList = this.groupByYear(this.invoices);
      } else {
        this.invoiceYearList = [];
      }
    }
  }

  getSubscriptionInterval() {
    const values = Object.values(PaymentFrequency);
    const index = values.indexOf(this.petData!.payment_interval) || 0;
    return `invoice-list.interval.${Object.keys(PaymentFrequency)[index]}`;
  }

  getSubscriptionCost() {
    return this.petData ? this.petData.yearly_gross_premium_pro_rata : 0;
  }

  getOpenAmount() {
    const openInvoices = this.invoices.filter(i => i.invoiceStatus === InvoiceStatus.OUTSTANDING);
    if (openInvoices.length === 0) {
      return null;
    }
    return openInvoices.reduce((sum, i) => sum + (i.totalAmount ?? 0), 0);
  }

  toggleYear(year: YearListItem<Invoice>) {
    year.expanded = !year.expanded;
  }

  checkInvoiceStatus(invoice: Invoice): 'paid' | 'outstanding' | 'future' | 'outdated' | 'aggregated' {
    switch (invoice.invoiceStatus) {
      case InvoiceStatus.PAID: return 'paid';
      case InvoiceStatus.OUTSTANDING: return 'outstanding';
      case InvoiceStatus.FUTURE: return 'future';
      case InvoiceStatus.OUTDATED: return 'outdated';
      case InvoiceStatus.AGGREGATED: return 'aggregated';
    }
  }

  formatInvoiceStatus(invoice: Invoice): string {
    return `invoice-list.status.${invoice.invoiceStatus}`;
  }

  parseDueDate(dueDate: string): Date {
    return parseDateCreated(dueDate);
  }

  isDownloading(invoice: Invoice): boolean {
    return !!invoice.bexio_invoice_id && this.downloading.has(invoice.bexio_invoice_id);
  }

  openInvoicePdf(invoice: Invoice) {
    // The whole row is clickable, so invoices without a PDF stay a no-op.
    const invoiceId = invoice.bexio_invoice_id;
    if (!invoice.bexio_pdf_available || !invoiceId || this.downloading.has(invoiceId)) {
      return;
    }

    // The PDF is no longer part of the contract list; it is fetched here so a
    // customer with years of invoices does not pull all of them up front.
    this.downloading.add(invoiceId);
    this.documentViewerService.openRemoteBase64Document(
      () => this.apiHttpService.getInvoicePdf(invoiceId),
      { fileName: invoice.invoiceNumber, errorMessageKeys: invoicePdfErrorMessageKeys }
    ).finally(() => this.downloading.delete(invoiceId));
  }

  private groupByYear(invoices: Invoice[]): YearListItem<Invoice>[] {
    let result: YearListItem<Invoice>[] = [];
    invoices.forEach(invoice => {
      const year = invoice.dateCreated instanceof Date ? invoice.dateCreated.getFullYear() : parseDateCreated(invoice.dateCreated).getFullYear();
      const yearListItem = result.find(item => item.year === year.toString());

      if (yearListItem) {
        yearListItem.items.push(invoice);
      } else {
        result.push({ year: year.toString(), expanded: false, items: [invoice] });
      }
    });

    // Sort the years in descending order
    result.sort((a, b) => +b.year - +a.year);

    result.forEach(item => {
      // Sort the invoices in descending order
      item.items.sort((a, b) => new Date(b.dateCreated instanceof Date ? b.dateCreated : parseDateCreated(b.dateCreated)).getTime() - new Date(a.dateCreated instanceof Date ? a.dateCreated : parseDateCreated(a.dateCreated)).getTime());
    })

    result[0].expanded = true;

    return result;
  }
}
